// @flow
import * as React from 'react';

import { toASCII } from 'punycode.js';
import { createSelector } from 'reselect';

import { selectProductsByTypeAndProcess } from '@gandi/cart';

import { useAppSelector } from 'src/hooks/useAppSelector';

import type { FullState } from 'src/types';

import { useDomainRenewableBoxes } from '../../modules/mailbox';
import type { Mailbox } from '../../types';

const EMPTY_ARRAY: Array<Mailbox> = [];

const selectFilteredMailboxes = createSelector(
  selectProductsByTypeAndProcess,
  (
    state: FullState,
    props: {
      process: string,
      productType: string,
      fqdn: string,
    },
  ) => props.fqdn,
  (products, fqdn) =>
    products.filter(product => product.identifier?.startsWith(fqdn)),
);

export function useMailboxToRenew(fqdn: string): {
  mailboxesToRenew: Array<Mailbox>,
  totalCount: number,
} {
  const infos = useDomainRenewableBoxes(fqdn);

  const mailboxes = infos?.mailboxes || EMPTY_ARRAY;
  const totalCount = infos?.totalCount || 0;

  const relatedMailboxesInTheCart = useAppSelector(state =>
    // $FlowFixMe[prop-missing]
    selectFilteredMailboxes(state, {
      productType: 'mailboxv2',
      process: 'renew',
      fqdn: toASCII(fqdn),
    }),
  );

  // remove related mailboxes which are already in the cart
  const mailboxesToRenew = React.useMemo(
    () =>
      mailboxes.filter(mailbox => {
        const email = `${mailbox.login}@${mailbox.domain}`;

        return !relatedMailboxesInTheCart.some(
          mailboxFromCart => mailboxFromCart.payload.email === email,
        );
      }),
    [mailboxes, relatedMailboxesInTheCart],
  );

  return {
    mailboxesToRenew,
    totalCount: totalCount - relatedMailboxesInTheCart.length,
  };
}
